import { useEffect, useState, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { CardLearningStatus, WordData } from '@shared/types/words';
import './FlashcardView.scss';
import Deck from '../components/Deck';
import { wordsDataAtom, wordToSaveAtom, wordsLoadMoreAtom, wordsLoadingAtom } from '@app/atoms/wordsAtoms';
import { selectedMediaAtom, selectedSeasonAtom, selectedEpisodeAtom } from '@app/atoms/selectedMediaAtoms';
import { getProgress, saveProgress } from '@app/utils/api';
import { buildShowIdentifier } from '@shared/utils/show';

interface FlashcardViewProps {
  onClose: () => void;
  mode?: 'main' | 'unstudied' | 'remembered';
}

const FlashcardView = ({ onClose, mode = 'main' }: FlashcardViewProps) => {
  const wordsData = useAtomValue(wordsDataAtom);
  const wordsLoading = useAtomValue(wordsLoadingAtom);
  const dispatchLoadMore = useSetAtom(wordsLoadMoreAtom);
  const setWordsData = useSetAtom(wordsDataAtom);
  const setWordToSave = useSetAtom(wordToSaveAtom);
  const [data, setData] = useState<WordData[]>(wordsData.words);
  const [cardStatuses, setCardStatuses] = useState<Record<string, CardLearningStatus>>({});
  const selectedMedia = useAtomValue(selectedMediaAtom);
  const selectedSeason = useAtomValue(selectedSeasonAtom);
  const selectedEpisode = useAtomValue(selectedEpisodeAtom);

  const [initialCardIndex, setInitialCardIndex] = useState(0);
  const [resumeLoadedCardsTotal, setResumeLoadedCardsTotal] = useState(0);
  const [resumeActiveCardsLimit, setResumeActiveCardsLimit] = useState(0);
  const progressRequestIdRef = useRef(0);
  const lastDeckIdRef = useRef('');
  const lastSavedIndexRef = useRef<number | null>(null);
  const pendingSavesRef = useRef<Set<Promise<unknown>>>(new Set());
  const showId = selectedMedia ? buildShowIdentifier(selectedMedia) : '';
  const deckId = showId
    ? `${showId}:${selectedSeason?.num ?? '0'}:${selectedEpisode?.num ?? '0'}`
    : '';

  const normalizeCardStatuses = (statuses: Record<string, CardLearningStatus | null> | null | undefined): Record<string, CardLearningStatus> => {
    if (!statuses) {
      return {};
    }
    const normalized: Record<string, CardLearningStatus> = {};
    for (const [cardId, status] of Object.entries(statuses)) {
      if (status === 'remembered' || status === 'not_remembered') {
        normalized[cardId] = status;
      }
    }
    return normalized;
  };

  useEffect(() => {
    if (wordsData.words.length) {
      setData(wordsData.words);
    }
    // TODO: what else?
  }, [wordsData]);

  useEffect(() => {
    if (!deckId || !showId || !wordsData.words.length) {
      return;
    }
    if (lastDeckIdRef.current === deckId) {
      return;
    }

    lastDeckIdRef.current = deckId;
    lastSavedIndexRef.current = null;
    setInitialCardIndex(0);
    setCardStatuses({});

    const requestId = ++progressRequestIdRef.current;
    (async () => {
      try {
        const progress = await getProgress(1, 1, {
          showId,
          season: selectedSeason?.num,
          episode: selectedEpisode?.num,
          includeHidden: true,
        });
        if (progressRequestIdRef.current !== requestId) {
          return;
        }
        const savedIndex = progress.list[0]?.details?.currentCardIndex ?? 0;
        const savedLoadedCardsTotal = progress.list[0]?.details?.loadedCardsTotal ?? 0;
        const savedActiveCardsLimit = progress.list[0]?.details?.activeCardsLimit ?? 0;
        setInitialCardIndex(savedIndex);
        setResumeLoadedCardsTotal(savedLoadedCardsTotal);
        setResumeActiveCardsLimit(savedActiveCardsLimit);
        setCardStatuses(normalizeCardStatuses(progress.list[0]?.details?.cardStatuses));
        lastSavedIndexRef.current = savedIndex;
      } catch (err) {
        console.error('Failed to load flashcard progress:', err);
      }
    })();
  }, [deckId, showId, selectedSeason, selectedEpisode, wordsData.words.length]);

  useEffect(() => {
    if (mode !== 'main') {
      return;
    }
    const requestedMainLimit = resumeActiveCardsLimit || resumeLoadedCardsTotal;
    const requiredCardsTotal = Math.max(requestedMainLimit, initialCardIndex);
    if (requiredCardsTotal <= wordsData.words.length) {
      return;
    }
    if (wordsLoading || wordsData.parts.size >= wordsData.totalParts) {
      return;
    }

    dispatchLoadMore();
  }, [mode, resumeLoadedCardsTotal, resumeActiveCardsLimit, initialCardIndex, wordsData.words.length, wordsData.parts.size, wordsData.totalParts, wordsLoading]);

  const handleEmpty = () => {
    console.log('cards: onEmpty');
  };
  const handleLike = (card: WordData) => {
    console.log("LIKE", card?.word);
  };
  const handleDislike = (card: WordData) => {
    console.log("DISLIKE", card?.word);
  };
  const handleSave = async(card: WordData) => {
    console.log("SAVE", card?.word);

    if (card?.saved) {
      return;
    }

    setWordToSave(card);
  };

  const trackPendingSave = (promise: Promise<unknown>) => {
    pendingSavesRef.current.add(promise);
    promise.finally(() => {
      pendingSavesRef.current.delete(promise);
    });
    return promise;
  };

  const handleClose = async () => {
    const pendingSaves = Array.from(pendingSavesRef.current);
    if (pendingSaves.length) {
      await Promise.allSettled(pendingSaves);
    }
    if (typeof onClose === 'function') {
      onClose();
    }
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      void handleClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleReset = () => {
    const firstPart = wordsData.parts.get(1);
    const firstBatchWords = firstPart?.words ? [...(firstPart.words as WordData[])] : [];
    const resetLoadedTotal = Math.max(1, Math.min(30, firstBatchWords.length || 30));

    if (firstPart) {
      setWordsData((prev) => ({
        words: firstBatchWords,
        parts: new Map([[1, firstPart]]),
        totalParts: prev.totalParts,
      }));
    }

    setData(firstBatchWords.length ? firstBatchWords : Array.from(data));
    setCardStatuses({});
    lastSavedIndexRef.current = 0;
    setInitialCardIndex(0);
    if (selectedMedia) {
      void trackPendingSave(saveProgress({
        show: selectedMedia,
        season: selectedSeason?.num,
        episode: selectedEpisode?.num,
        screenmediaId: selectedMedia.dbId,
        details: {
          currentCardIndex: 0,
          cardStatuses: null,
          loadedCardsTotal: resetLoadedTotal,
          activeCardsLimit: resetLoadedTotal,
        },
      }).catch(err => console.error('Failed to reset flashcard progress:', err)));
    }

    if (mode !== 'main') {
      handleClose();
    }
  }

  const handleProgressUpdate = ({ currentCardIndex }: { currentCardIndex: number }) => {
    if (!selectedMedia) {
      return;
    }
    if (lastSavedIndexRef.current === currentCardIndex) {
      return;
    }

    lastSavedIndexRef.current = currentCardIndex;

    void trackPendingSave(saveProgress({
      show: selectedMedia,
      season: selectedSeason?.num,
      episode: selectedEpisode?.num,
      screenmediaId: selectedMedia.dbId,
      details: {
        currentCardIndex,
      },
    }).catch(err => console.error('Failed to save flashcard progress:', err)));
  };

  const handleCardStatusUpdate = (card: WordData, status: CardLearningStatus | null) => {
    if (!selectedMedia) {
      return;
    }

    setCardStatuses((prev) => {
      if ((prev[card.id] ?? null) === status) {
        return prev;
      }
      if (status === null) {
        const next = { ...prev };
        delete next[card.id];
        return next;
      }
      return { ...prev, [card.id]: status };
    });

    void trackPendingSave(saveProgress({
      show: selectedMedia,
      season: selectedSeason?.num,
      episode: selectedEpisode?.num,
      screenmediaId: selectedMedia.dbId,
      details: {
        cardStatuses: { [card.id]: status },
      },
    }).catch(err => console.error('Failed to save flashcard card status:', err)));
  };

  const mainModeLimit = Math.max(
    1,
    Math.min(
      resumeActiveCardsLimit || resumeLoadedCardsTotal || data.length,
      data.length
    )
  );
  const visibleDeckCards = mode === 'main' ? data.slice(0, mainModeLimit) : data;

  const cardsWithStatuses = visibleDeckCards.map((card) => {
    const status = cardStatuses[card.id];
    return status ? { ...card, learningStatus: status } : card;
  });

  return (<div className="modal flashcard-modal" style={{display: 'block'}}>
    <div className="modal-dialog modal-fullscreen flashcard-modal__dialog">
      <div className="modal-content flashcard-modal__content">
        <div className="modal-body flashcard-modal__body" style={{padding: 0}}>
        <Deck
          cards={cardsWithStatuses}
          mode={mode}
          deckId={deckId}
          initialCurrentCardIndex={initialCardIndex}
          initialCardStatuses={cardStatuses}
          onProgress={handleProgressUpdate}
          onCardStatusChange={handleCardStatusUpdate}
          onLike={(card) => handleLike(card)}
          onDislike={(card) => handleDislike(card)}
          onSave={(card) => handleSave(card)}
          onEmpty={handleEmpty}
            onClose={handleClose}
            onReset={handleReset}
            loadMoreEnabled={wordsData.parts.size < wordsData.totalParts}
          />
        </div>
      </div>
    </div>
  </div>);
};

export default FlashcardView;
